import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDate(date: Date | string): string {
  const d = new Date(date);
  return d.toLocaleDateString('pt-BR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function formatDateTime(date: Date | string): string {
  const d = new Date(date);
  return d.toLocaleString('pt-BR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean;
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

export function generateId(): string {
  return Math.random().toString(36).substr(2, 9);
}

export function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.substr(0, maxLength) + '...';
}

export function capitalizeFirst(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function getInitials(name: string): string {
  return name
    .split(' ')
    .map(word => word.charAt(0))
    .join('')
    .toUpperCase()
    .substr(0, 2);
}

/**
 * Normaliza tags que podem vir em diferentes formatos (array, string JSON, string simples)
 * Remove aspas desnecessárias, quebras de linha e retorna um array limpo de strings
 */
export function normalizeTags(tags: string[] | string | any): string[] {
  if (!tags) return [];

  const sanitizeTagValue = (value: unknown): string => {
    let cleaned = String(value ?? '')
      .replace(/[\n\r\t]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    if (!cleaned) return '';

    // Remove camadas externas comuns de serialização.
    let previous = '';
    while (cleaned && cleaned !== previous) {
      previous = cleaned;

      if (
        (cleaned.startsWith('"') && cleaned.endsWith('"')) ||
        (cleaned.startsWith("'") && cleaned.endsWith("'"))
      ) {
        cleaned = cleaned.slice(1, -1).trim();
      }

      if (
        (cleaned.startsWith('[') && cleaned.endsWith(']')) ||
        (cleaned.startsWith('{') && cleaned.endsWith('}'))
      ) {
        cleaned = cleaned.slice(1, -1).trim();
      }
    }

    return cleaned
      .replace(/^[\[\]\{\}"'\s]+/, '')
      .replace(/[\[\]\{\}"'\s]+$/, '')
      .trim();
  };

  const extractFromString = (raw: string): string[] => {
    const cleaned = raw
      .replace(/[\n\r\t]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    if (!cleaned) return [];

    // Tenta parse progressivo para strings serializadas múltiplas vezes.
    let candidate: unknown = cleaned;
    for (let attempt = 0; attempt < 2; attempt += 1) {
      if (typeof candidate !== 'string') break;
      try {
        candidate = JSON.parse(candidate);
      } catch {
        break;
      }
    }
    if (candidate !== cleaned) {
      return collectTags(candidate);
    }

    // Extrai tags entre aspas se houver.
    const quotedMatches = Array.from(
      cleaned.matchAll(/"([^"]+)"|'([^']+)'/g)
    ).map(match => sanitizeTagValue(match[1] ?? match[2] ?? ''));
    const quotedTags = quotedMatches.filter(Boolean);
    if (quotedTags.length > 0) {
      return quotedTags;
    }

    // Fallback para listas separadas por vírgula.
    if (cleaned.includes(',')) {
      return cleaned
        .replace(/^\[|\]$/g, '')
        .split(',')
        .map(part => sanitizeTagValue(part))
        .filter(Boolean);
    }

    const singleTag = sanitizeTagValue(cleaned);
    return singleTag ? [singleTag] : [];
  };

  const collectTags = (input: unknown): string[] => {
    if (!input) return [];

    if (Array.isArray(input)) {
      return input.flatMap(item => {
        if (typeof item === 'string') {
          const normalizedItem = item
            .replace(/[\n\r\t]+/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();

          // Trata casos como ['["tag-a"', '"tag-b"]'].
          if (
            normalizedItem.includes(',') ||
            normalizedItem.includes('[') ||
            normalizedItem.includes(']') ||
            normalizedItem.includes('"') ||
            normalizedItem.includes("'")
          ) {
            const extracted = extractFromString(normalizedItem);
            if (extracted.length > 0) {
              return extracted;
            }
          }
        }

        const sanitized = sanitizeTagValue(item);
        return sanitized ? [sanitized] : [];
      });
    }

    if (typeof input === 'string') {
      return extractFromString(input);
    }

    const sanitized = sanitizeTagValue(input);
    return sanitized ? [sanitized] : [];
  };

  return collectTags(tags).filter(Boolean);
}

/**
 * Utilitários para cálculo de preços de tokens
 * Baseado em análise de custos de APIs de IA (GPT-5.2-Pro) com margem de lucro de 50%
 */

export type TokenPricingScenario = 'low' | 'medium' | 'high';

/**
 * Custos por milhão de tokens (em USD)
 * Baseado em preços do GPT-5.2-Pro (2025/2026)
 * Fonte: https://openai.com/api/pricing/
 */
export const TOKEN_COSTS_USD = {
  low: {
    // Modelos Mini - para tarefas simples
    input: 0.15,
    output: 0.60,
    mixed: 0.50, // Média entre input e output
  },
  medium: {
    // Cenário misto padrão (GPT-4o) - mantido para referência
    input: 1.25,
    output: 10.00,
    mixed: 5.00, // Média entre input e output
  },
  high: {
    // GPT-5.2-Pro (padrão atual)
    // Input: US$ 21 por milhão
    // Output: US$ 168 por milhão
    // Mixed: 25% input + 75% output = (0.25 × 21) + (0.75 × 168) = 5.25 + 126 = US$ 131.25
    input: 21.00,
    output: 168.00,
    mixed: 131.25, // Cenário misto: 25% input, 75% output
  },
} as const;

/**
 * Taxa de câmbio USD para BRL (pode ser atualizada conforme necessário)
 */
export const USD_TO_BRL_RATE = 5.0;

/**
 * Margem de lucro desejada (50%)
 */
export const PROFIT_MARGIN = 1.5; // 1.5 = 50% de margem (custo * 1.5)

/**
 * Calcula o custo em USD para uma quantidade de tokens
 * @param tokensAmount Quantidade de tokens (em milhões)
 * @param scenario Cenário de preço (low, medium, high)
 * @param type Tipo de uso (input, output, mixed)
 * @returns Custo em USD
 */
export function calculateTokenCostUSD(
  tokensAmount: number,
  scenario: TokenPricingScenario = 'medium',
  type: 'input' | 'output' | 'mixed' = 'mixed'
): number {
  const tokensInMillions = tokensAmount / 1_000_000;
  const costPerMillion = TOKEN_COSTS_USD[scenario][type];
  return tokensInMillions * costPerMillion;
}

/**
 * Calcula o preço de venda em USD com margem de lucro
 * @param tokensAmount Quantidade de tokens (em milhões)
 * @param scenario Cenário de preço (low, medium, high)
 * @param type Tipo de uso (input, output, mixed)
 * @returns Preço de venda em USD
 */
export function calculateTokenPriceUSD(
  tokensAmount: number,
  scenario: TokenPricingScenario = 'medium',
  type: 'input' | 'output' | 'mixed' = 'mixed'
): number {
  const cost = calculateTokenCostUSD(tokensAmount, scenario, type);
  return cost * PROFIT_MARGIN;
}

/**
 * Converte preço de USD para BRL
 * @param priceUSD Preço em USD
 * @param exchangeRate Taxa de câmbio (padrão: 5.0)
 * @returns Preço em BRL
 */
export function convertUSDToBRL(
  priceUSD: number,
  exchangeRate: number = USD_TO_BRL_RATE
): number {
  return priceUSD * exchangeRate;
}

/**
 * Calcula o preço de venda em BRL para uma quantidade de tokens
 * @param tokensAmount Quantidade de tokens
 * @param scenario Cenário de preço (low, medium, high)
 * @param type Tipo de uso (input, output, mixed)
 * @param exchangeRate Taxa de câmbio USD/BRL (padrão: 5.0)
 * @returns Preço de venda em BRL
 */
export function calculateTokenPriceBRL(
  tokensAmount: number,
  scenario: TokenPricingScenario = 'medium',
  type: 'input' | 'output' | 'mixed' = 'mixed',
  exchangeRate: number = USD_TO_BRL_RATE
): number {
  const priceUSD = calculateTokenPriceUSD(tokensAmount, scenario, type);
  return convertUSDToBRL(priceUSD, exchangeRate);
}

/**
 * Calcula o preço recomendado para um pacote de tokens
 * Usa o cenário high (GPT-5.2-Pro) como padrão
 * @param tokensAmount Quantidade de tokens
 * @returns Preço recomendado em BRL
 */
export function getRecommendedTokenPrice(tokensAmount: number): number {
  return calculateTokenPriceBRL(tokensAmount, 'high', 'mixed');
}

/**
 * Valida se um preço está dentro de uma faixa aceitável
 * @param tokensAmount Quantidade de tokens
 * @param priceBRL Preço em BRL a validar
 * @param tolerance Tolerância percentual (padrão: 20%)
 * @returns true se o preço está dentro da faixa aceitável
 */
export function validateTokenPrice(
  tokensAmount: number,
  priceBRL: number,
  tolerance: number = 0.2
): { isValid: boolean; recommended: number; min: number; max: number } {
  const recommended = getRecommendedTokenPrice(tokensAmount);
  const min = recommended * (1 - tolerance);
  const max = recommended * (1 + tolerance);

  return {
    isValid: priceBRL >= min && priceBRL <= max,
    recommended,
    min,
    max,
  };
}

/**
 * Formata uma quantidade de tokens de forma legível
 * @param tokens Quantidade de tokens
 * @returns String formatada (ex: "1.5M", "500K", "1000")
 */
export function formatTokenAmount(tokens: number): string {
  // Mostrar valores inteiros com separador de milhar para facilitar leitura
  return tokens.toLocaleString('pt-BR', { maximumFractionDigits: 0 });
}

/**
 * Exemplos de preços recomendados para pacotes comuns
 * Baseado no cenário médio (misto) com margem de 50%
 */
export const RECOMMENDED_PACKAGES = {
  // 100 mil tokens
  small: {
    tokens: 100_000,
    priceBRL: getRecommendedTokenPrice(100_000), // ~R$ 0.38
  },
  // 500 mil tokens
  medium: {
    tokens: 500_000,
    priceBRL: getRecommendedTokenPrice(500_000), // ~R$ 1.88
  },
  // 1 milhão de tokens
  large: {
    tokens: 1_000_000,
    priceBRL: getRecommendedTokenPrice(1_000_000), // ~R$ 3.75
  },
  // 5 milhões de tokens
  xlarge: {
    tokens: 5_000_000,
    priceBRL: getRecommendedTokenPrice(5_000_000), // ~R$ 18.75
  },
  // 10 milhões de tokens
  xxlarge: {
    tokens: 10_000_000,
    priceBRL: getRecommendedTokenPrice(10_000_000), // ~R$ 37.50
  },
} as const;

/**
 * Interface para criação de pacotes recomendados
 */
export interface RecommendedPackageConfig {
  name: string;
  description: string;
  ds_tokens_amount: number;
  price_brl: number;
  scenario?: TokenPricingScenario;
  type?: 'input' | 'output' | 'mixed';
}

/**
 * Gera pacotes recomendados com valores calculados baseados na fórmula de preços
 * @param tokenAmounts Array de quantidades de tokens para criar pacotes
 * @param scenario Cenário de preço (padrão: 'high' - GPT-5.2-Pro)
 * @param type Tipo de uso (padrão: 'mixed')
 * @returns Array de configurações de pacotes recomendados
 */
export function generateRecommendedPackages(
  tokenAmounts: number[],
  scenario: TokenPricingScenario = 'high',
  type: 'input' | 'output' | 'mixed' = 'mixed'
): RecommendedPackageConfig[] {
  return tokenAmounts.map(tokens => {
    const priceBRL = calculateTokenPriceBRL(tokens, scenario, type);
    const tokensFormatted = formatTokenAmount(tokens);
    
    // Gerar nome baseado na quantidade
    let name = '';
    let description = '';
    
    if (tokens < 500_000) {
      name = `Pacote Pequeno - ${tokensFormatted} tokens`;
      description = 'Ideal para testes e uso pessoal';
    } else if (tokens < 2_000_000) {
      name = `Pacote Médio - ${tokensFormatted} tokens`;
      description = 'Perfeito para projetos pequenos e médios';
    } else if (tokens < 10_000_000) {
      name = `Pacote Grande - ${tokensFormatted} tokens`;
      description = 'Recomendado para uso profissional';
    } else {
      name = `Pacote Premium - ${tokensFormatted} tokens`;
      description = 'Para empresas e uso intensivo';
    }
    
    return {
      name,
      description,
      ds_tokens_amount: tokens,
      price_brl: Math.round(priceBRL * 100) / 100, // Arredondar para 2 casas decimais
      scenario,
      type,
    };
  });
}

/**
 * Extrai JSON de uma string que pode conter markdown code blocks
 * Remove code blocks markdown (```json ... ``` ou ``` ... ```) e faz parse do JSON
 * @param str String que pode conter JSON dentro de code blocks markdown
 * @returns Objeto JSON parseado
 */
export function extractJsonFromMarkdown(str: string): any {
  if (!str || typeof str !== 'string') {
    throw new Error('String inválida fornecida');
  }

  let cleaned = str.trim();

  // Remove code blocks markdown (```json ... ``` ou ``` ... ```)
  // Pode ter espaços ou quebras de linha antes/depois
  const codeBlockRegex = /^```(?:json)?\s*\n?([\s\S]*?)\n?```$/;
  const match = cleaned.match(codeBlockRegex);
  
  if (match && match[1]) {
    // Extraiu conteúdo do code block
    cleaned = match[1].trim();
  } else {
    // Tenta remover apenas os delimitadores se estiverem presentes
    cleaned = cleaned
      .replace(/^```json\s*/i, '')
      .replace(/^```\s*/, '')
      .replace(/\s*```$/, '')
      .trim();
  }

  // Tenta fazer parse do JSON limpo
  try {
    return JSON.parse(cleaned);
  } catch (parseError) {
    // Se falhar, tenta parse direto da string original (caso não tenha code blocks)
    try {
      return JSON.parse(str);
    } catch (originalError) {
      throw new Error(
        `Erro ao parsear JSON: ${parseError instanceof Error ? parseError.message : 'Erro desconhecido'}`
      );
    }
  }
}

/**
 * Pacotes recomendados padrão com valores calculados
 * Baseado no cenário high (misto) - GPT-5.2-Pro
 * 
 * Cálculo: 25% input (US$ 21/M) + 75% output (US$ 168/M) = US$ 131.25/M
 * Com margem de 50%: US$ 196.875/M
 * Em BRL (taxa 5.0): R$ 984.375/M
 * 
 * Valores calculados:
 * - 100K tokens: R$ 98,44
 * - 200K tokens: R$ 196,88
 * - 500K tokens: R$ 492,19
 * - 750K tokens: R$ 738,28
 * - 1M tokens: R$ 984,38
 * - 1.5M tokens: R$ 1.476,56
 */
export const DEFAULT_RECOMMENDED_PACKAGES: RecommendedPackageConfig[] = [
  {
    name: 'Pacote Iniciante - 100K tokens',
    description: 'Ideal para testes e experimentação',
    ds_tokens_amount: 100_000,
    price_brl: 98.44,
  },
  {
    name: 'Pacote Básico - 200K tokens',
    description: 'Perfeito para uso pessoal e projetos pequenos',
    ds_tokens_amount: 200_000,
    price_brl: 196.88,
  },
  {
    name: 'Pacote Intermediário - 500K tokens',
    description: 'Para usuários que precisam de mais recursos',
    ds_tokens_amount: 500_000,
    price_brl: 492.19,
  },
  {
    name: 'Pacote Avançado - 750K tokens',
    description: 'Ideal para profissionais e pequenas equipes',
    ds_tokens_amount: 750_000,
    price_brl: 738.28,
  },
  {
    name: 'Pacote Padrão - 1M tokens',
    description: 'Recomendado para a maioria dos usuários',
    ds_tokens_amount: 1_000_000,
    price_brl: 984.38,
  },
  {
    name: 'Pacote Profissional - 1.5M tokens',
    description: 'Para uso profissional e projetos maiores',
    ds_tokens_amount: 1_500_000,
    price_brl: 1476.56,
  },
];
